/**
 * VB Server - Users API
 *
 * User profile and organization membership operations.
 *
 * @example
 * import { getCurrentUser, updateCurrentUser, listOrgUsers, updateUserOrg, removeUserFromOrg } from '@/api/server/users';
 *
 * const me = await getCurrentUser();
 * await updateCurrentUser({ name: 'New Name' });
 */

import { serverGet, serverPost, serverPut, serverPatch, serverDelete } from './client';
import type { OrgRole } from '../../v2/services/auth';

export type { OrgRole };

// =============================================================================
// Types
// =============================================================================

/**
 * User organization membership
 */
export interface UserOrgMembership {
  org_id: string;
  org_role: OrgRole;
  name?: string;
  image?: string;
}

/**
 * User entity from vb-server
 */
export interface User {
  user_id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  status: number;
  created: string;
  modified: string;
  organizations?: UserOrgMembership[];
}

/**
 * Organization user (user with org membership info)
 */
export interface OrgUser {
  user_id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  role?: string; // system-level role (e.g. 'super-admin') from users table
  org_role: OrgRole;
  status: number;
  created: string;
}

/**
 * Input for updating current user profile
 */
export interface UpdateCurrentUserInput {
  name?: string;
  image?: string | null;
}

/**
 * Input for updating user's org membership
 */
export interface UpdateUserOrgInput {
  org_id: string;
  org_role?: OrgRole;
}

/**
 * Response from updating user org membership
 */
export interface UserOrgResponse {
  user_id: string;
  org_id: string;
  org_role: OrgRole;
}

/**
 * System-level role for user promotion/demotion
 */
export type SystemRole = 'user' | 'super-admin';

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current authenticated user
 *
 * @returns Current user with organization memberships
 */
export async function getCurrentUser(): Promise<User> {
  return serverGet<User>('/users/me');
}

/**
 * Update current user's profile
 *
 * @param data - Profile updates (name, image)
 * @returns Updated user
 */
export async function updateCurrentUser(data: UpdateCurrentUserInput): Promise<User> {
  return serverPut<User>('/users/me', data);
}

/**
 * List all users in an organization
 *
 * @param orgId - Organization ID
 * @param options - Optional filters (includeInactive)
 * @returns Array of organization users
 */
export async function listOrgUsers(
  orgId: string,
  options?: { includeInactive?: boolean }
): Promise<OrgUser[]> {
  const params = new URLSearchParams();
  if (options?.includeInactive) params.set('include_inactive', 'true');
  const qs = params.toString();
  return serverGet<OrgUser[]>(`/orgs/${orgId}/users${qs ? `?${qs}` : ''}`);
}

/**
 * Update a user's organization membership (role, permissions)
 *
 * Requires admin role in the organization.
 *
 * @param userId - User ID to update
 * @param data - Membership updates
 * @returns Updated membership
 */
export async function updateUserOrg(
  userId: string,
  data: UpdateUserOrgInput
): Promise<UserOrgResponse> {
  return serverPatch<UserOrgResponse>(`/users/${userId}`, data);
}

/**
 * Remove a user from an organization
 *
 * Requires admin role in the organization.
 * Cannot remove yourself.
 *
 * @param userId - User ID to remove
 * @param orgId - Organization ID to remove from
 */
export async function removeUserFromOrg(userId: string, orgId: string): Promise<void> {
  await serverDelete(`/users/${userId}`, { org_id: orgId });
}

/**
 * Update a user's role within an organization
 *
 * @param userId - User ID
 * @param orgId - Organization ID
 * @param role - New org role (admin, editor, viewer)
 * @returns Updated membership
 */
export async function updateUserRole(
  userId: string,
  orgId: string,
  role: OrgRole
): Promise<UserOrgResponse> {
  return updateUserOrg(userId, { org_id: orgId, org_role: role });
}

/**
 * Update a user's system-level role (super-admin only)
 *
 * @param userId - User ID
 * @param role - New system role ('user' or 'super-admin')
 * @returns Updated user
 */
export async function updateUserSystemRole(userId: string, role: SystemRole): Promise<User> {
  return serverPut<User>(`/users/${userId}/system-role`, { role });
}

/**
 * Restore a deactivated user's org membership
 *
 * @param orgId - Organization ID
 * @param userId - User ID to restore
 */
export async function restoreUserOrg(orgId: string, userId: string): Promise<void> {
  await serverPost(`/orgs/${orgId}/users/${userId}/restore`, {});
}
